import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { VideoItem } from "@/types/video";
import { formatRating, typeLabel } from "@/lib/format";

interface HeroCarouselProps {
  items: VideoItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const go = (index: number) => setActive((index + items.length) % items.length);

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative h-[420px] w-full md:h-[560px]">
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={item.backdrop}
              alt={`${item.title} 推荐大图`}
              title={`${item.title} 在线观看`}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-primary-950/30" />
          </div>
        ))}

        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto w-full max-w-site px-4 pb-10 md:px-6 md:pb-14">
            <div className="max-w-2xl">
              <div className="mb-3 flex items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2 py-0.5 text-xs font-semibold text-background-50">
                  最新推荐
                </span>
                <span className="rounded-md bg-background-50/15 px-2 py-0.5 text-xs font-medium text-background-50">
                  {typeLabel(items[active].type)}
                </span>
              </div>
              <h2 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">
                {items[active].title}
              </h2>
              <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-background-200">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-400" />
                  {formatRating(items[active].rating)}
                </span>
                <span>{items[active].year}</span>
                <span>{items[active].region}</span>
                <span>{items[active].genres.join(" / ")}</span>
              </p>
              <p className="mt-3 hidden max-w-xl text-sm leading-relaxed text-background-300 sm:block">
                {items[active].synopsis}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${items[active].id}`}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-fill text-base" />
                  查看详情
                </Link>
                <Link
                  to={`/category/${items[active].type}`}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                >
                  更多同类
                </Link>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(active - 1)}
          aria-label="上一张"
          className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/30 bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70 md:left-6 md:h-10 md:w-10"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          onClick={() => go(active + 1)}
          aria-label="下一张"
          className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/30 bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70 md:right-6 md:h-10 md:w-10"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>

        <div className="absolute bottom-4 right-4 flex items-center gap-2 md:right-6">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => go(index)}
              aria-label={`切换到 ${item.title}`}
              className={
                index === active
                  ? "h-1.5 w-6 rounded-full bg-accent-500"
                  : "h-1.5 w-1.5 rounded-full bg-background-50/50 transition-all hover:bg-background-50/80"
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
}